import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Modal,
  Typography,
  Snackbar,
  Alert,
  Paper,
  Chip,
  Stack,
  Backdrop,
  Fade,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SidebarMenu from "./AdminPanel/SidebarMenu";
import TopBar from "./AdminPanel/TopBar";
import { useNavigate, useLocation } from "react-router-dom";
import ClientTable from "./ClientForm/ClientTable";

function getCSRFToken() {
  const name = "csrftoken";
  const cookies = document.cookie.split(";");

  for (let cookie of cookies) {
    const trimmed = cookie.trim();

    if (trimmed.startsWith(name + "=")) {
      return decodeURIComponent(trimmed.substring(name.length + 1));
    }
  }

  return null;
}

const mapBackendListToPersonas = (data) => {
  const list = data?.personas || [];

  return list.map((p) => ({
    id: p.id,
    tipo: p.tipo || "N",
    razon_social: p.razon_social || "",
    nombre_comercial: p.nombre_comercial || "",
    cedula: p.cedula || "",
    ruc: p.ruc || "",
    telefono: p.telefono || "",
    correo: p.correo || "",
    direccion: p.direccion || "",
    es_cliente: !!p.es_cliente,
    es_proveedor: !!p.es_proveedor,
    es_empleado: !!p.es_empleado,
    es_extranjero: !!p.es_extranjero,
  }));
};

const AdminClient = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [personas, setPersonas] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [rolFilter, setRolFilter] = useState("todos");
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [deleteId, setDeleteId] = useState(null);

  const [open, setOpen] = useState(false);
  const [submenuOpen, setSubmenuOpen] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    type: "success",
    message: "",
  });

  useEffect(() => {
    const s = location.state?.snackbar;

    if (s?.message) {
      setSnackbar({
        open: true,
        type: s.type || "success",
        message: s.message,
      });

      navigate(".", { replace: true, state: {} });
    }
  }, [location.state, navigate]);

  const fetchPersonas = async () => {
    setLoading(true);

    try {
      const res = await fetch("/clientes/listar/", {
        credentials: "include",
      });

      const data = await res.json();

      setPersonas(mapBackendListToPersonas(data));
    } catch {
      setPersonas([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPersonas();
  }, []);

  useEffect(() => {
    const term = searchTerm.toLowerCase();

    const f = personas.filter((p) => {
      const matchSearch =
        p.razon_social.toLowerCase().includes(term) ||
        p.nombre_comercial.toLowerCase().includes(term) ||
        p.cedula.toLowerCase().includes(term) ||
        p.ruc.toLowerCase().includes(term) ||
        p.telefono.toLowerCase().includes(term) ||
        p.correo.toLowerCase().includes(term);

      const matchRol =
        rolFilter === "todos" ||
        (rolFilter === "cliente" && p.es_cliente) ||
        (rolFilter === "proveedor" && p.es_proveedor) ||
        (rolFilter === "empleado" && p.es_empleado);

      return matchSearch && matchRol;
    });

    setFiltered(f);
    setPage(0);
  }, [searchTerm, rolFilter, personas]);

  const handleDelete = async () => {
    try {
      const res = await fetch(`/clientes/eliminar/${deleteId}/`, {
        method: "DELETE",
        headers: {
          "X-CSRFToken": getCSRFToken(),
        },
        credentials: "include",
      });

      if (res.ok) {
        setSnackbar({
          open: true,
          type: "success",
          message: "Persona eliminada correctamente.",
        });

        fetchPersonas();
      } else {
        setSnackbar({
          open: true,
          type: "error",
          message: "No se pudo eliminar la persona.",
        });
      }
    } catch {
      setSnackbar({
        open: true,
        type: "error",
        message: "Error al eliminar la persona.",
      });
    } finally {
      setDeleteId(null);
    }
  };

  const handleLogout = async () => {
    try {
      await fetch("/sistema/logout/", {
        method: "POST",
        credentials: "include",
      });
    } catch {}

    localStorage.clear();
    navigate("/login");
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "#f5f6fa" }}>
      <SidebarMenu
        open={open}
        setOpen={setOpen}
        submenuOpen={submenuOpen}
        setSubmenuOpen={setSubmenuOpen}
        navigate={navigate}
        handleLogout={handleLogout}
      />

      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <TopBar open={open} setOpen={setOpen} />

        <Box sx={{ width: "95%", mx: "auto", mt: 3, mb: 5 }}>
          <Box sx={{ display: "flex", justifyContent: "flex-start", mb: 3 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate("/admin-panel/clientes/nuevo")}
              sx={{
                textTransform: "none",
                bgcolor: "#1565C0",
                "&:hover": { bgcolor: "#0D47A1" },
                borderRadius: 2,
                px: 2.2,
                fontWeight: 700,
                boxShadow: "0 8px 20px rgba(21,101,192,0.22)",
              }}
            >
              Administrar
            </Button>
          </Box>

          <Paper
            sx={{
              p: 2.5,
              borderRadius: 3,
              boxShadow: "0 10px 30px rgba(15,23,42,0.06)",
            }}
          >
            <Stack
              direction={{ xs: "column", md: "row" }}
              justifyContent="space-between"
              spacing={2}
              mb={2}
            >
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: 19, color: "#0f172a" }}>
                  Personas
                </Typography>
                <Typography sx={{ fontSize: 13, color: "#64748b", mt: 0.4 }}>
                  Administración de personas registradas
                </Typography>
              </Box>

              <Chip
                label={`${filtered.length} registros`}
                color="info"
                variant="outlined"
                sx={{ fontWeight: 700, borderRadius: 2 }}
              />
            </Stack>

            <ClientTable
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              rolFilter={rolFilter}
              setRolFilter={setRolFilter}
              filtered={filtered}
              loading={loading}
              page={page}
              setPage={setPage}
              rowsPerPage={rowsPerPage}
              setRowsPerPage={setRowsPerPage}
              handleView={(p) => {
                navigate(`/admin-panel/clientes/${p.id}`);
              }}
              handleDeleteConfirm={setDeleteId}
            />
          </Paper>
        </Box>
      </Box>

      <Modal
        open={!!deleteId}
        onClose={() => setDeleteId(null)}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{ timeout: 250 }}
      >
        <Fade in={!!deleteId}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              bgcolor: "#fff",
              p: 3,
              borderRadius: 3,
              width: { xs: "92%", sm: 380 },
              boxShadow: "0 24px 60px rgba(15,23,42,0.28)",
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 700, color: "#0f172a" }} mb={1.5}>
              ¿Eliminar persona?
            </Typography>

            <Typography sx={{ color: "#475569" }} mb={3}>
              Esta acción no se puede deshacer.
            </Typography>

            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
              <Button
                variant="outlined"
                onClick={() => setDeleteId(null)}
                sx={{ textTransform: "none", fontWeight: 700 }}
              >
                Cancelar
              </Button>

              <Button
                variant="contained"
                color="error"
                onClick={handleDelete}
                sx={{ textTransform: "none", fontWeight: 700 }}
              >
                Eliminar
              </Button>
            </Box>
          </Box>
        </Fade>
      </Modal>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          severity={snackbar.type}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminClient;